import React from 'react';
import './DispatchBoard.scss';
import {
    Badge,
    Button,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    ListGroup,
    ListGroupItem,
    ListGroupItemHeading,
    ListGroupItemText,
    UncontrolledTooltip,
    UncontrolledDropdown,
} from 'reactstrap';
import moment from 'moment';
import cls from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle, faRecycle, faExclamationTriangle, faInfoCircle, faCheck,
    faEllipsisVertical, faFlag,
} from '@fortawesome/free-solid-svg-icons';
import { crewLeadLabel, weekdayShort } from '@s20/i18n';
import { pluralize } from '@s20/common/util/l10n';
import { SmallButton, IconButton } from '../common/forms/FormElements';
import { Alert } from '../common/Modal';
import { EquipmentLogModalBody } from '../timesheet/EquipmentLogModalBody';
import { Can } from '../Can';
import EquipmentJobAssignment from './EquipmentJobAssignment';
import StaffJobAssignment from './StaffJobAssignment';
import { TransferJob } from './TransferJob';
import { AddShiftResources } from './EditJobResourcesModal';
import { FlagJob } from './FlagJob';
import { SetEmergency } from './SetEmergency';

export const viewEquipmentLog = (timesheetId, job) => {
    Alert.show({
        title: 'Timesheet Equipment Log',
        size: 'xl',
        body: (
            <EquipmentLogModalBody
                timesheetId={timesheetId}
                jobStartTimeOffset={job.startTimeOffset}
                timeZone={job.timeZone}
            />
        ),
    });
};

const handleOverrideButtonClick = (selectedOverride = null) => {
    Alert.show({
        title: `View Changes for ${selectedOverride?.overrideDay}`,
        body: (
            <ListGroup>
                {Boolean(selectedOverride?.startTime) && (
                    <ListGroupItem>
                        <ListGroupItemText className="mb-1">
                            Start Time
                        </ListGroupItemText>
                        <ListGroupItemHeading className="mb-0">
                            {moment(selectedOverride.startTime, 'HH:mm').format('hh:mm A')}
                        </ListGroupItemHeading>
                    </ListGroupItem>
                )}

                {Boolean(selectedOverride?.foremenName) && (
                    <ListGroupItem>
                        <ListGroupItemText className="mb-1">
                            Foreman
                        </ListGroupItemText>
                        <ListGroupItemHeading className="mb-0">
                            {selectedOverride.foremenName}
                        </ListGroupItemHeading>
                    </ListGroupItem>
                )}

                {Boolean(selectedOverride?.requestorName) && (
                    <ListGroupItem>
                        <ListGroupItemText className="mb-1">
                            Requested By
                        </ListGroupItemText>
                        <ListGroupItemHeading className="mb-0">
                            {selectedOverride.requestorName}
                        </ListGroupItemHeading>
                    </ListGroupItem>
                )}

                {Boolean(selectedOverride?.dispatchOfficeName) && (
                    <ListGroupItem>
                        <ListGroupItemText className="mb-1">
                            Dispatch Office
                        </ListGroupItemText>
                        <ListGroupItemHeading className="mb-0">
                            {selectedOverride.dispatchOfficeName}
                        </ListGroupItemHeading>
                    </ListGroupItem>
                )}

                {Boolean(selectedOverride?.dispatchPortName) && (
                    <ListGroupItem>
                        <ListGroupItemText className="mb-1">
                            Dispatch Port
                        </ListGroupItemText>
                        <ListGroupItemHeading className="mb-0">
                            {selectedOverride.dispatchPortName}
                        </ListGroupItemHeading>
                    </ListGroupItem>
                )}

                {Boolean(selectedOverride?.projectName) && (
                    <ListGroupItem>
                        <ListGroupItemText className="mb-1">
                            Project Name
                        </ListGroupItemText>
                        <ListGroupItemHeading className="mb-0">
                            {selectedOverride.projectName}
                        </ListGroupItemHeading>
                    </ListGroupItem>
                )}

                {Boolean(selectedOverride?.projectManagerName) && (
                    <ListGroupItem>
                        <ListGroupItemText className="mb-1">
                            Project Manager
                        </ListGroupItemText>
                        <ListGroupItemHeading className="mb-0">
                            {selectedOverride.projectManagerName}
                        </ListGroupItemHeading>
                    </ListGroupItem>
                )}

                {Boolean(selectedOverride?.inspectorName) && (
                    <ListGroupItem>
                        <ListGroupItemText className="mb-1">
                            Inspector Name
                        </ListGroupItemText>
                        <ListGroupItemHeading className="mb-0">
                            {selectedOverride.inspectorName}
                        </ListGroupItemHeading>
                    </ListGroupItem>
                )}

                {Boolean(selectedOverride?.subcontractorName) && (
                    <ListGroupItem>
                        <ListGroupItemText className="mb-1">
                            Subcontractor
                        </ListGroupItemText>
                        <ListGroupItemHeading className="mb-0">
                            {selectedOverride.subcontractorName}
                        </ListGroupItemHeading>
                    </ListGroupItem>
                )}

                {Boolean(selectedOverride?.workOrderNumber) && (
                    <ListGroupItem>
                        <ListGroupItemText className="mb-1">
                            Work Order Number
                        </ListGroupItemText>
                        <ListGroupItemHeading className="mb-0">
                            {selectedOverride.workOrderNumber}
                        </ListGroupItemHeading>
                    </ListGroupItem>
                )}

                {Boolean(selectedOverride?.jobTypeId) && (
                    <ListGroupItem>
                        <ListGroupItemText className="mb-1">
                            Job Type
                        </ListGroupItemText>
                        <ListGroupItemHeading className="mb-0">
                            {selectedOverride?.jobTypeDescription}
                        </ListGroupItemHeading>
                    </ListGroupItem>
                )}

                {Boolean(selectedOverride?.meetingAddress) && (
                    <ListGroupItem>
                        <ListGroupItemText className="mb-1">
                            Meeting Address
                        </ListGroupItemText>
                        <ListGroupItemHeading className="mb-0">
                            {selectedOverride.meetingAddress}
                        </ListGroupItemHeading>
                    </ListGroupItem>
                )}

                {Boolean(selectedOverride?.jobAddress) && (
                    <ListGroupItem>
                        <ListGroupItemText className="mb-1">
                            Job Address
                        </ListGroupItemText>
                        <ListGroupItemHeading className="mb-0">
                            {selectedOverride.jobAddress}
                        </ListGroupItemHeading>
                    </ListGroupItem>
                )}

                {Boolean(selectedOverride?.notes) && (
                    <ListGroupItem>
                        <ListGroupItemText className="mb-1">
                            Notes
                        </ListGroupItemText>
                        <ListGroupItemHeading className="mb-0">
                            {selectedOverride.notes}
                        </ListGroupItemHeading>
                    </ListGroupItem>
                )}
            </ListGroup>
        ),
    });
};

export default class DispatchJobAssignments extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedOverride: null,
        };
    }

    selectedDayHasOverride = (selectedDay) => {
        const { job } = this.props;
        const dayInt = moment(selectedDay).day();

        // Use override from assignments if available
        if (job.assignments?.[dayInt]?.override) {
            return job.assignments[dayInt].override;
        }

        // Fallback to legacy override lookup
        if (!job.overrides || job.overrides.length === 0) {
            return null;
        }

        return job.overrides.find((override) =>
            moment(override.overrideDate, moment.ISO_8601).isSame(moment(selectedDay), 'date'),
        );
    };

    getCertificationsForDay = (dayIndex) => {
        const { job } = this.props;

        return (job.certifications || []).filter((cert) => cert.day === dayIndex);
    };

    getIsCertificationSelected = (certificationId) => {
        const { filters } = this.props;

        return !!filters.find((f) => (f.certIds || []).includes(certificationId));
    };

    render() {
        const {
            job,
            selectedWeekRangeDays,
            jobIndex,
            tenantSettings,
            onRemoveDayJob,
            onRefreshDayJobs,
            isDropZoneValid,
            dragging,
            onSlotSelect,
            pulsingSlotId,
            dispatchLocations,
            onCertificationButtonClick,
            dispatchEditPermission,
            equipmentAuditLogPermission,
            crossDispatchPermission,
        } = this.props;

        return (
            <div className="jobAssignmentsTableContainer">
                <table
                    key={jobIndex}
                    className="table table-sm jobAssignmentsTable mb-0"
                >
                    <thead>
                        <tr>
                            {(selectedWeekRangeDays ?? []).map((day) => {
                                const dayInt = day.getDay();

                                const showCancel =
                                    !!job.assignments[dayInt].canEdit &&
                                    (
                                        !!job.assignments[dayInt].staff.length ||
                                        !!job.assignments[dayInt].equipment.length
                                    );
                                const dayAssignment = job.assignments[dayInt];
                                const showActionsMenu =
                                    (showCancel && dispatchEditPermission) ||
                                    (dayAssignment.canEdit && crossDispatchPermission) ||
                                    dispatchEditPermission;
                                const isFlagged = !!dayAssignment.isFlagged;
                                const flags = dayAssignment.flags ?? [];
                                const flagTooltipId = `day-flag-${job.id}-${dayInt}`;
                                const hasAnyStaff = (dayAssignment.staff?.length ?? 0) > 0;
                                const hasCrewLead = dayAssignment.staff?.some((staffMember) => staffMember.isCrewLead);
                                const uniqueId = `missing-crew-lead-${job.id}-${dayInt}`;

                                const override = this.selectedDayHasOverride(day);
                                const abbreviation = weekdayShort(dayInt);

                                return (
                                    <th key={day}>
                                        <div className="w-100 job-assignment-table-header d-flex flex-row flex-nowrap">
                                            <span className="text-left flex-fill">
                                                {override ? (
                                                    <Button
                                                        color="link"
                                                        className="font-weight-bold text-uppercase"
                                                        role="button"
                                                        onClick={() => handleOverrideButtonClick(override)}
                                                    >
                                                        <FontAwesomeIcon
                                                            id={`day-override-${job.id}-${override.id}`}
                                                            icon={faExclamationTriangle}
                                                            className="text-warning ml-1"
                                                        />
                                                        <span className="text-warning">
                                                            {abbreviation}
                                                        </span>
                                                        <UncontrolledTooltip
                                                            placement="top"
                                                            target={`day-override-${job.id}-${override.id}`}
                                                        >
                                                            The job details for this day have been changed.
                                                        </UncontrolledTooltip>
                                                    </Button>
                                                ) : abbreviation}
                                            </span>

                                            {hasAnyStaff && (
                                                !hasCrewLead ? (
                                                    <span
                                                        className={`text-muted mr-2 crew-lead-badge unassigned ${selectedWeekRangeDays.length > 1 ? 'compact-badge' : ''}`}
                                                    >
                                                        <FontAwesomeIcon
                                                            id={uniqueId}
                                                            icon={faInfoCircle}
                                                            size="sm"
                                                        />
                                                        <span className="ml-1 badge-label">{`Missing ${crewLeadLabel(tenantSettings)}`}</span>
                                                        <UncontrolledTooltip
                                                            key={`tooltip-${dayInt}`}
                                                            placement="top"
                                                            target={uniqueId}
                                                            delay={{ show: 0, hide: 0 }}
                                                        >
                                                            {`Missing ${crewLeadLabel(tenantSettings)}`}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                ) : (
                                                    <span
                                                        className={`text-muted mr-2 crew-lead-badge assigned ${selectedWeekRangeDays.length > 1 ? 'compact-badge' : ''}`}
                                                    >
                                                        <FontAwesomeIcon
                                                            id={uniqueId}
                                                            icon={faCheckCircle}
                                                            size="sm"
                                                        />
                                                        <span className="ml-1 badge-label">{`${crewLeadLabel(tenantSettings)} Assigned`}</span>
                                                        <UncontrolledTooltip
                                                            key={`tooltip-${dayInt}`}
                                                            placement="top"
                                                            target={uniqueId}
                                                            delay={{ show: 0, hide: 0 }}
                                                        >
                                                            {`${crewLeadLabel(tenantSettings)} Assigned`}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )
                                            )}

                                            <span className="text-muted">
                                                {`${day.getMonth() + 1}/${day.getDate()}`}
                                            </span>
                                            {isFlagged && (
                                                <>
                                                    <span
                                                        id={flagTooltipId}
                                                        className="ml-1 text-danger"
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faFlag}
                                                            size="sm"
                                                        />
                                                        {flags.length > 1 && (
                                                            <span className="ml-1 font-weight-bold">{flags.length}</span>
                                                        )}
                                                    </span>
                                                    <UncontrolledTooltip
                                                        placement="top"
                                                        target={flagTooltipId}
                                                    >
                                                        {flags.length === 0 ? (
                                                            'This job day has been flagged'
                                                        ) : (
                                                            <div className="text-left">
                                                                <div className="font-weight-bold mb-1">
                                                                    {`${pluralize(flags.length, 'flag')} on this day:`}
                                                                </div>
                                                                {flags.map((flag, flagIndex) => (
                                                                    <div key={flag.flagId ?? flagIndex}>
                                                                        {flag.flagType}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </UncontrolledTooltip>
                                                </>
                                            )}
                                            {showActionsMenu ? (
                                                <UncontrolledDropdown className="ml-2">
                                                    <DropdownToggle
                                                        tag="span"
                                                        data-toggle="dropdown"
                                                        role="menu"
                                                    >
                                                        <IconButton
                                                            type="button"
                                                            size="lg"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faEllipsisVertical}
                                                                className="text-dark"
                                                                size="lg"
                                                            />
                                                        </IconButton>
                                                    </DropdownToggle>

                                                    <DropdownMenu end>
                                                        {showCancel && dispatchEditPermission && (
                                                            <DropdownItem
                                                                className="text-danger"
                                                                onClick={() => onRemoveDayJob(job, dayInt)}
                                                            >
                                                                Cancel Job
                                                            </DropdownItem>
                                                        )}

                                                        {job.assignments[dayInt].canEdit && crossDispatchPermission && (
                                                            <TransferJob
                                                                job={job}
                                                                day={dayInt}
                                                                dispatchLocations={dispatchLocations}
                                                                onSuccess={onRefreshDayJobs}
                                                            />
                                                        )}

                                                        {!!dispatchEditPermission && dayAssignment.isShiftOverEstimate && (
                                                            <AddShiftResources
                                                                jobId={job.id}
                                                                selectedDates={[day]}
                                                            />
                                                        )}

                                                        <Can
                                                            I="edit"
                                                            a="dispatch"
                                                        >
                                                            <FlagJob
                                                                job={job}
                                                                day={day}
                                                                onSuccess={onRefreshDayJobs}
                                                            />
                                                        </Can>
                                                    </DropdownMenu>
                                                </UncontrolledDropdown>
                                            ) : null}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        <tr>
                            {(selectedWeekRangeDays ?? []).map((day) => {
                                const dayInt = day.getDay();
                                const certs = this.getCertificationsForDay(dayInt);

                                const dayAssignments = job.assignments[dayInt];
                                const hasAsns =
                                    !!dayAssignments.staff.length || !!dayAssignments.equipment.length;

                                const dayIsFlagged = !!job.assignments[dayInt].isFlagged;
                                const dayCellClass = cls(
                                    dayIsFlagged && 'day-flagged',
                                    job.assignments[dayInt].isEmergency && 'day-emergency',
                                );

                                return (
                                    <td
                                        key={day}
                                        className={dayCellClass}
                                    >
                                        <div className="d-flex">
                                            {!!hasAsns && !job.assignments[dayInt].canEdit && (
                                                <div className="d-flex flex-column flex-fill">
                                                    <span
                                                        className="text-info mt-1 mb-1"
                                                        title="Once the timesheet has been filled out by the crew for this job, date, and start time, no further dispatching modifications can be done."
                                                    >
                                                        <i className="mr-1 fa fa-clock" />
                                                        Timesheet Complete
                                                    </span>
                                                </div>
                                            )}

                                            {hasAsns && dispatchEditPermission ? (
                                                <div className="d-flex flex-column flex-fill">
                                                    <SetEmergency
                                                        job={job}
                                                        day={dayInt}
                                                        onSuccess={onRefreshDayJobs}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="flex-fill" />
                                            )}

                                            {Boolean(hasAsns) && (
                                                <div>
                                                    <Button
                                                        color="link"
                                                        size="sm"
                                                        className="site-link text-danger mt-1 mb-1 d-inline"
                                                        onClick={() => this.props.onEditJobWorkflowClicked(job, dayInt)}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faRecycle}
                                                            className="mr-1"
                                                        />
                                                        {!job.assignments[dayInt].canEdit || !dispatchEditPermission ? 'View Job Workflow' : 'Edit Job Workflow'}
                                                    </Button>
                                                </div>
                                            )}
                                        </div>

                                        {certs.length > 0 && (
                                            <div className="d-flex justify-content-end mb-2">
                                                {certs.map((cert) => (
                                                    <SmallButton
                                                        key={cert.id}
                                                        className="border mr-0 ml-2"
                                                        onClick={() => onCertificationButtonClick(cert.id)}
                                                        selected={this.getIsCertificationSelected(cert.id)}
                                                    >
                                                        {cert.quantity > 0 ? (
                                                            <Badge
                                                                color="info"
                                                                className="mr-1"
                                                            >{cert.quantity}</Badge>
                                                        ) : (
                                                            <FontAwesomeIcon
                                                                icon={faCheck}
                                                                className="mr-1 text-success"
                                                            />
                                                        )}

                                                        {cert.abbreviation}
                                                    </SmallButton>
                                                ))}
                                            </div>
                                        )}

                                        <div className="assignmentsWrapper">
                                            <div className="assignments">
                                                {
                                                    // Staff-employee assignments
                                                    // _.sortBy(job.assignments[dayInt].staff, s => s.name).map((group, index) => {
                                                    job.assignments[dayInt].staff.map((group, index) => {
                                                        const key = `${jobIndex}_${dayInt}_staff_${index}`;
                                                        let dispatchLocation = null;

                                                        if (group.employeeDispatchOffice) {
                                                            dispatchLocation = group.employeeDispatchOffice;
                                                        }

                                                        return (
                                                            <StaffJobAssignment
                                                                key={key}
                                                                job={job}
                                                                jobIndex={jobIndex}
                                                                day={day}
                                                                assignmentIndex={index}
                                                                group={group}
                                                                dispatchLocation={dispatchLocation}
                                                                onRemoveAssignment={this.props.onRemoveAssignment}
                                                                setCrewLead={this.props.setCrewLead}
                                                                crewLeadTimesheets={this.props.crewLeadTimesheets}
                                                                dragging={dragging}
                                                                isDropZoneValid={isDropZoneValid}
                                                                onSlotSelect={onSlotSelect}
                                                                pulsingSlotId={pulsingSlotId}
                                                                dispatchEditPermission={dispatchEditPermission}
                                                                onMobileAssignmentClick={this.props.onMobileAssignmentClick}
                                                                tenantSettings={tenantSettings}
                                                            />
                                                        );
                                                    })
                                                }
                                                {
                                                    // Any standalone required equipment (not needing to be assigned to staff)
                                                    // _.sortBy(job.assignments[dayInt].equipment, e => e.description).map((requiredEquipmentType, index) => {
                                                    job.assignments[dayInt].equipment.map((requiredEquipmentType, index) => {
                                                        const key = `${jobIndex}_${dayInt}_equipment_${index}`;
                                                        return (
                                                            <EquipmentJobAssignment
                                                                key={key}
                                                                job={job}
                                                                jobIndex={jobIndex}
                                                                day={day}
                                                                dayAssignments={job.assignments[dayInt]}
                                                                assignmentIndex={index}
                                                                requiredEquipmentType={requiredEquipmentType}
                                                                onEquipmentAssignmentChange={this.props.onEquipmentAssignmentChange}
                                                                onRemoveAssignment={this.props.onRemoveAssignment}
                                                                isDropZoneValid={isDropZoneValid}
                                                                dragging={dragging}
                                                                onSlotSelect={onSlotSelect}
                                                                pulsingSlotId={pulsingSlotId}
                                                                dispatchEditPermission={dispatchEditPermission}
                                                                onMobileAssignmentClick={this.props.onMobileAssignmentClick}
                                                                getEligibleStaffForEquipment={this.props.getEligibleStaffForEquipment}
                                                                getEligibleEquipmentForEquipment={this.props.getEligibleEquipmentForEquipment}
                                                            />
                                                        );
                                                    })
                                                }
                                            </div>
                                        </div>

                                        {tenantSettings.timesheetEquipmentAuditEnabled && equipmentAuditLogPermission && dayAssignments.timesheetId && (
                                            <div className="text-center mt-1">
                                                <Button
                                                    color="link"
                                                    size="sm"
                                                    className="site-link text-info p-0"
                                                    onClick={() => viewEquipmentLog(dayAssignments.timesheetId, job)}
                                                >
                                                    View Equipment Log
                                                </Button>
                                            </div>
                                        )}
                                    </td>
                                );
                            })
                            }
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }
}
